import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * CUSTOM (HOMYCASA): Dialog scroll lock uses `html[scroll-lock]` (overflow: hidden)
 * instead of the theme default `body { position: fixed; top: -scrollY }`.
 *
 * Body-fixed scroll lock breaks `position: sticky` on the PDP header and product
 * details column when a modal opens mid-scroll. This matches the approach already
 * used by the header drawer (`assets/header-drawer.js`) and `html[scroll-lock]` in
 * `assets/base.css`.
 *
 * @type {number}
 */
let openDialogCount = 0;

/**
 * CUSTOM: Whether another UI surface still needs scroll lock after dialogs close.
 * @returns {boolean}
 */
function hasOtherScrollLockConsumers() {
  return Boolean(
    document.querySelector('details[scroll-lock][open]') ||
      document.querySelector('#Details-menu-drawer-container.menu-open') ||
      document.querySelector('[data-cascade-open="true"]')
  );
}

/** CUSTOM: Apply scroll lock when a dialog opens. */
export function acquireDialogScrollLock() {
  openDialogCount++;
  document.documentElement.setAttribute('scroll-lock', '');
}

/**
 * CUSTOM: Release scroll lock acquired by acquireDialogScrollLock().
 */
export function releaseDialogScrollLock() {
  openDialogCount = Math.max(0, openDialogCount - 1);
  releaseScrollLockIfIdle();
}

/**
 * CUSTOM: Remove scroll lock only when no dialogs remain and no drawer/details
 * consumer still needs it.
 */
function releaseScrollLockIfIdle() {
  if (openDialogCount > 0 || hasOtherScrollLockConsumers()) return;

  document.documentElement.removeAttribute('scroll-lock');
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {boolean} */
  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // If the dialog node is removed while open (e.g. section morph/replace), release
    // scroll lock — closeDialog() never runs so openDialogCount would leak otherwise.
    const { dialog } = this.refs;
    if (dialog?.open && this.#ownsScrollLock) {
      openDialogCount = Math.max(0, openDialogCount - 1);
      this.#ownsScrollLock = false;
      releaseScrollLockIfIdle();
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   * @param {{ instant?: boolean }} [options] - Skip open animation (e.g. when re-opening after a morph).
   */
  showDialog(options = {}) {
    const { dialog } = this.refs;
    const { instant = false } = options;

    if (dialog.open) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (dialog.open) return;

      if (instant) {
        dialog.classList.add('dialog-instant');
        dialog.style.setProperty('--dialog-drawer-opening-animation', 'none');
      }

      // CUSTOM: see module note above — avoids body position:fixed sticky offset bug.
      acquireDialogScrollLock();
      this.#ownsScrollLock = true;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    try {
      // Drop instant-open overrides before the close animation runs.
      dialog.classList.remove('dialog-instant');
      dialog.style.removeProperty('--dialog-drawer-opening-animation');

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      if (this.#ownsScrollLock) {
        openDialogCount = Math.max(0, openDialogCount - 1);
        this.#ownsScrollLock = false;
        // CUSTOM: keep lock if a menu drawer or another dialog still needs it.
        releaseScrollLockIfIdle();
      }

      dialog.close();
      dialog.classList.remove('dialog-closing', 'dialog-instant');
      dialog.style.removeProperty('--dialog-drawer-opening-animation');

      this.dispatchEvent(new DialogCloseEvent());
    } finally {
      this.#isClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          // CUSTOM: do not unlock while a dialog (or other consumer) is still open.
          releaseScrollLockIfIdle();
        }
      }
    }
  },
  { capture: true }
);
